function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input.field__input');

    // Move the dropdown panel out of this element and onto <body> so that
    // no CSS transform on any ancestor can break position:fixed.
    var panel = this.querySelector('.predictive-search-content');
    if (panel) {
      document.body.appendChild(panel);
      panel.style.display = 'none';
    }
    this.dropdownEl = panel;

    // After the panel is moved, re-query result containers inside the panel.
    this.predictiveSearchResults        = this.dropdownEl && this.dropdownEl.querySelector('[data-predictive-search]');
    this.predictiveSearchResultsArticle = this.dropdownEl && this.dropdownEl.querySelector('[data-predictive-search-article]');

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce(() => {
      this.onChange();
    }, 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));

    // focusout on the custom element — also check the detached panel
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup',    this.onKeyup.bind(this));
    this.addEventListener('keydown',  this.onKeydown.bind(this));

    // Close button inside the bar wrapper
    const bar = this.closest('.search-inline-bar');
    const closeBtn = bar && bar.querySelector('.search-inline-bar__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        if (typeof closeSearchBar === 'function') {
          closeSearchBar();
        } else {
          document.body.classList.remove('active_search');
          this.close(true);
        }
      });
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();
    if (!searchTerm.length) { this.close(true); return; }
    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();
    if (!searchTerm.length) return;
    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      // Active element could be inside the detached panel, so check both.
      var ae = document.activeElement;
      if (!this.contains(ae) && !(this.dropdownEl && this.dropdownEl.contains(ae))) {
        this.close();
      }
    }, 150);
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
    }
    event.preventDefault();
    switch (event.code) {
      case 'ArrowUp':   this.switchOption('up');  break;
      case 'ArrowDown': this.switchOption('down'); break;
      case 'Enter':     this.selectOption();       break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') event.preventDefault();
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;
    const moveUp        = direction === 'up';
    const selected      = this.dropdownEl ? this.dropdownEl.querySelector('[aria-selected="true"]') : null;
    const allItems      = this.dropdownEl ? this.dropdownEl.querySelectorAll('li') : [];
    let   active        = allItems[0];

    if (moveUp && !selected) return;
    if (!moveUp && selected) active = selected.nextElementSibling   || allItems[0];
    else if (moveUp)         active = selected.previousElementSibling || allItems[allItems.length - 1];
    if (!active || active === selected) return;

    active.setAttribute('aria-selected', true);
    if (selected) selected.setAttribute('aria-selected', false);
    this.input.setAttribute('aria-activedescendant', active.id);
  }

  selectOption() {
    if (!this.dropdownEl) return;
    const el = this.dropdownEl.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');
    if (el) el.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/ /g, '-').toLowerCase();

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product&section_id=predictive-search`)
      .then(r => { if (!r.ok) { this.close(); throw new Error(r.status); } return r.text(); })
      .then(text => {
        const el = new DOMParser().parseFromString(text, 'text/html').querySelector('#predictive-search-product');
        if (el) this.renderSearchResults(el.innerHTML, queryKey);
      })
      .catch(e => { this.close(); throw e; });

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=article&section_id=predictive-search`)
      .then(r => { if (!r.ok) { this.close(); throw new Error(r.status); } return r.text(); })
      .then(text => {
        const el = new DOMParser().parseFromString(text, 'text/html').querySelector('#predictive-search-blog');
        if (el) this.renderSearchResultsArticle(el.innerHTML, queryKey);
      })
      .catch(e => { this.close(); throw e; });
  }

  renderSearchResults(markup, queryKey) {
    if (this.predictiveSearchResults) this.predictiveSearchResults.innerHTML = markup;
    this.setAttribute('results', true);

    var searchUrl = '/search?q=' + queryKey + '&type=product';

    // Update "view all" link inside the panel
    var viewAll = this.dropdownEl && this.dropdownEl.querySelector('[data-predictive-search] [data-view-all-product]');
    if (viewAll) viewAll.setAttribute('href', searchUrl);

    // Inject / update the mobile "View all results" button at the bottom of the panel
    if (this.dropdownEl) {
      var existing = this.dropdownEl.querySelector('.search-viewall-mobile');
      if (!existing) {
        var footer = document.createElement('div');
        footer.className = 'search-viewall-mobile';
        footer.innerHTML = '<a href="' + searchUrl + '">View all results</a>';
        this.dropdownEl.appendChild(footer);
      } else {
        var a = existing.querySelector('a');
        if (a) a.setAttribute('href', searchUrl);
      }
    }

    this.open();
  }

  renderSearchResultsArticle(markup, queryKey) {
    if (this.predictiveSearchResultsArticle) {
      this.predictiveSearchResultsArticle.innerHTML = markup;
      this.setAttribute('results', true);
      var viewAll = this.dropdownEl && this.dropdownEl.querySelector('[data-predictive-search-article] [data-view-all-article]');
      if (viewAll) viewAll.setAttribute('href', '/search?q=' + queryKey + '&type=article');
      this.open();
    }
  }

  open() {
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    if (!this.dropdownEl) return;

    var isDesktop = window.innerWidth >= 1200;
    var vh = window.innerHeight;
    var top, maxH;

    if (isDesktop) {
      var topRow = document.querySelector('.header-desktop .header-top-row');
      top  = topRow ? Math.round(topRow.getBoundingClientRect().bottom) : 80;
      maxH = Math.round(Math.min(vh - top - 8, vh * 0.70));
    } else {
      var mobileBar = document.querySelector('.header-mobile .header-center__inner');
      top  = mobileBar ? Math.round(mobileBar.getBoundingClientRect().bottom) : 60;
      maxH = Math.round(Math.min(vh * 0.45, vh - top - 8));
    }

    this.dropdownEl.style.display   = 'block';
    this.dropdownEl.style.position  = 'fixed';
    this.dropdownEl.style.top       = top + 'px';
    this.dropdownEl.style.left      = '0';
    this.dropdownEl.style.width     = '100%';
    this.dropdownEl.style.maxHeight = maxH + 'px';
    this.dropdownEl.style.zIndex    = '9999';
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    if (this.dropdownEl) {
      var sel = this.dropdownEl.querySelector('[aria-selected="true"]');
      if (sel) sel.setAttribute('aria-selected', false);
    }
    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    if (this.dropdownEl) this.dropdownEl.style.display = 'none';
  }
}

customElements.define('predictive-search', PredictiveSearch);
